import React, { useMemo } from "react";
import BoardCardShell from "../BoardCardShell";
import BoardChartRegion from "../BoardChartRegion";
import BoardCardState from "../BoardCardState";
import InsightsChart from "features/analytics/studio/components/charts/InsightsChart";
import InsightsDataTable from "features/analytics/studio/components/charts/InsightsDataTable";
import MetricCards from "features/analytics/studio/components/charts/MetricCards";
import { formatNumber } from "shared/utils/numberFormatter";

const BADGE = { label: "Insights", bg: "var(--feedback-info-bg)", color: "var(--feedback-info)" };

function sortAndLimitBreakdowns(resultSeries, definition) {
  if (!resultSeries?.length) return resultSeries;
  const isBreakdown = resultSeries[0]?.breakdowns?.length > 0;
  if (!isBreakdown) return resultSeries;
  const showMax = definition?.breakdownShowMax === true;
  const maxLimit = showMax ? 50 : Math.min(Math.max(1, Number(definition?.breakdownLimit) || 10), 50);
  const sortOrder = definition?.breakdownSortOrder || "average-desc";
  const sortFn = (() => {
    switch (sortOrder) {
      case "segment-a-z": return (a, b) => (a.breakdownKey || "").localeCompare(b.breakdownKey || "");
      case "segment-z-a": return (a, b) => (b.breakdownKey || "").localeCompare(a.breakdownKey || "");
      case "average-asc": return (a, b) =>
        (a.data||[]).reduce((s,p)=>s+(p.value||0),0)/Math.max(1,(a.data||[]).length) -
        (b.data||[]).reduce((s,p)=>s+(p.value||0),0)/Math.max(1,(b.data||[]).length);
      default: return (a, b) => (b.total ?? 0) - (a.total ?? 0);
    }
  })();
  return resultSeries.map((m) => {
    if (!m.breakdowns?.length) return m;
    return { ...m, breakdowns: [...m.breakdowns].sort(sortFn).slice(0, maxLimit) };
  });
}

function flattenForMetricCards(resultSeries, limit = 10) {
  if (!resultSeries?.length) return resultSeries;
  if (!resultSeries[0]?.breakdowns?.length) return resultSeries;
  const out = [];
  resultSeries.forEach((ms, mi) => {
    const id = String.fromCharCode(65 + mi);
    const segs = (ms.breakdowns || []).map((b) => {
      const label = b.breakdownValues?.length ? b.breakdownValues.join("/") : (b.breakdownKey || ms.metricName);
      return {
        metricId: ms.metricId,
        metricName: label,
        metricIdentifier: id,
        displayLabel: label,
        fullMetricName: ms.metricName,
        originalBreakdownKey: b.breakdownKey || label,
        data: b.data || [],
        summary: { total: b.total || 0 },
        breakdownKey: b.breakdownKey,
        breakdownValues: b.breakdownValues,
      };
    });
    segs.sort((a, b) => (b.summary?.total ?? 0) - (a.summary?.total ?? 0));
    out.push(...segs.slice(0, limit));
  });
  return out;
}

function flattenForTable(resultSeries, limit = 50) {
  if (!resultSeries?.length) return resultSeries;
  if (!resultSeries[0]?.breakdowns?.length) return resultSeries;
  const out = [];
  resultSeries.forEach((ms) => {
    const segs = (ms.breakdowns || []).map((b) => {
      const sum = (b.data || []).reduce((s, p) => s + (p.value || 0), 0);
      return {
        metricId: ms.metricId,
        fullMetricName: ms.metricName,
        breakdownKey: b.breakdownKey,
        breakdownValues: b.breakdownValues ?? (b.breakdownKey ? [b.breakdownKey] : []),
        data: b.data || [],
        summary: { total: b.total ?? sum },
      };
    });
    segs.sort((a, b) => (b.summary?.total ?? 0) - (a.summary?.total ?? 0));
    out.push(...segs.slice(0, limit));
  });
  return out;
}

export default function InsightsBoardCard({ card, result, onEdit, onDelete, readOnly }) {
  const definition = card?.queryDsl?.definition;
  const rawSeries = result?.data?.series;
  const breakdownLimit = definition?.breakdownShowMax ? 50 : Math.max(1, Number(definition?.breakdownLimit) || 10);

  const series = useMemo(() => {
    if (!rawSeries?.length) return rawSeries;
    if (rawSeries[0]?.breakdowns?.length) return sortAndLimitBreakdowns(rawSeries, definition);
    return rawSeries;
  }, [rawSeries, definition]);

  const hasData = !!(result?.data && Array.isArray(series) && series.length > 0);
  const s0 = series?.[0];

  const cardVis = card?.visualization && typeof card.visualization === "object" ? card.visualization : {};
  const dslVis = card?.queryDsl?.visualization && typeof card.queryDsl.visualization === "object" ? card.queryDsl.visualization : {};
  const mergedVis = { ...dslVis, ...cardVis };
  const chartConfig = mergedVis.config && typeof mergedVis.config === "object" ? mergedVis.config : null;
  const chartType = mergedVis.chartType || "line";

  const defMetrics = card?.queryDsl?.definition?.metrics;
  const metricsWithAxis = Array.isArray(defMetrics)
    ? defMetrics.map((m) => ({ id: m.id, axis: m.axis || "left" }))
    : null;
  const chartData = hasData ? { ...result.data, series, metrics: metricsWithAxis } : undefined;

  // Subtitle: metric names + totals
  const subtitle = useMemo(() => {
    if (!hasData) return null;
    if (series.length === 1) {
      const parts = [s0.metricName || "Metric"];
      if (s0.summary?.total != null) parts.push(`Total: ${formatNumber(s0.summary.total)}`);
      const delta = s0.delta;
      if (delta?.percentage != null) parts.push(`${delta.percentage >= 0 ? "+" : ""}${Number(delta.percentage).toFixed(1)}%`);
      return parts.join(" • ");
    }
    return series.map((s) => {
      const t = s.summary?.total;
      return t != null ? `${s.metricName || "Metric"} (${formatNumber(t)})` : (s.metricName || "Metric");
    }).join(", ");
  }, [hasData, series, s0]);

  const actions = readOnly ? [] : [
    ...(onEdit ? [{ label: "Edit", icon: "edit_pencil", onClick: onEdit }] : []),
    ...(onDelete ? [{ label: "Delete", icon: "delete", onClick: onDelete, danger: true }] : []),
  ];

  // State resolution
  const state = !result ? "loading" : result.error ? "error" : null;
  const errorMsg = typeof result?.error === "string" ? result.error : "Could not load data";

  return (
    <BoardCardShell
      title={card.title || "Untitled"}
      subtitle={subtitle}
      badge={BADGE}
      actions={actions}
      readOnly={readOnly}
    >
      {state ? (
        <BoardCardState state={state} message={state === "error" ? errorMsg : undefined} />
      ) : chartType === "metric" && hasData ? (
        <BoardChartRegion className="overflow-auto">
          {() => {
            const flat = flattenForMetricCards(series, breakdownLimit);
            return (
              <div className="w-full h-full overflow-auto">
                <MetricCards
                  series={flat}
                  seriesColors={chartConfig?.colors}
                  hideTitleRow={flat.length === 1}
                />
              </div>
            );
          }}
        </BoardChartRegion>
      ) : chartType === "table" ? (
        <BoardChartRegion className="overflow-auto">
          {() =>
            hasData ? (
              series[0]?.breakdowns?.length ? (
                <InsightsDataTable
                  allSeries={flattenForTable(series, breakdownLimit)}
                  seriesColors={chartConfig?.colors}
                  breakdownProperties={s0?.breakdownProperties}
                  breakdownFormat
                  chartType="table"
                  fullTotalsByMetric={series.reduce((acc, s) => {
                    const id = s.metricId ?? s.metricName;
                    if (id != null && s.summary?.total != null) acc[id] = s.summary.total;
                    return acc;
                  }, {})}
                />
              ) : (
                <InsightsDataTable
                  allSeries={series}
                  seriesColors={chartConfig?.colors}
                  chartType="table"
                />
              )
            ) : (
              <BoardCardState state="empty" />
            )
          }
        </BoardChartRegion>
      ) : (
        <BoardChartRegion>
          {({ height }) =>
            hasData ? (
              <InsightsChart
                data={chartData}
                chartType={chartType}
                config={chartConfig}
                height={Math.max(60, height)}
                timeGranularity={card?.queryDsl?.time?.interval}
                boardMode
              />
            ) : (
              <BoardCardState state="empty" />
            )
          }
        </BoardChartRegion>
      )}
    </BoardCardShell>
  );
}
